import { createContext, useContext, useEffect, useState, type PropsWithChildren } from "react";

type AuthorGoogleAnalyticsContextType = {
    active: boolean;
    setActive: (active: boolean) => void;
};

const AuthorGoogleAnalyticsContext = createContext<AuthorGoogleAnalyticsContextType>({
    active: false,
    setActive: () => {}
});

/**
 * Tracks whether the current page loads the author's own Google Analytics, so ours can stay off.
 * @param props The app tree
 * @returns The provider
 */
export function AuthorGoogleAnalyticsProvider({ children }: PropsWithChildren) {
    const [active, setActive] = useState(false);

    return <AuthorGoogleAnalyticsContext.Provider value={{ active, setActive }}>{children}</AuthorGoogleAnalyticsContext.Provider>;
}

/**
 * Reads whether the current page loads the author's own Google Analytics.
 * @returns True while a page has registered the author's measurement id
 */
export function useAuthorGoogleAnalyticsActive() {
    return useContext(AuthorGoogleAnalyticsContext).active;
}

/**
 * Registers the author's Google Analytics for as long as the calling component is mounted.
 * @param active Whether the page loads the author's measurement id
 */
export function useRegisterAuthorGoogleAnalytics(active: boolean) {
    const { setActive } = useContext(AuthorGoogleAnalyticsContext);

    useEffect(() => {
        if (!active) return;
        setActive(true);

        return () => setActive(false);
    }, [active]);
}
